import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { db } from '../firebase';

function newQuestionId() {
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

const defaultQuestion = () => ({
  id: newQuestionId(),
  prompt: '',
  type: 'single',
  optionsText: 'Yes\nNo',
});

function parentLabel(parents, uid) {
  const p = parents.find((x) => x.id === uid);
  if (!p) return uid;
  const n = (p.name || p.displayName || '').trim();
  const e = (p.email || '').trim();
  if (n && e) return `${n} (${e})`;
  return n || e || uid;
}

function parentRowFields(parents, uid) {
  const p = parents.find((x) => x.id === uid);
  const name = p ? (p.name || p.displayName || '').trim() : '';
  const email = p ? (p.email || '').trim() : '';
  return { name, email };
}

function surveyToForm(data) {
  const questions = Array.isArray(data.questions) ? data.questions : [];
  return {
    title: typeof data.title === 'string' ? data.title : '',
    description: typeof data.description === 'string' ? data.description : '',
    active: data.active === true,
    showLiveResults: data.showLiveResults !== false,
    allowEdit: data.allowEdit === true,
    anonymousPublicResults: data.anonymousPublicResults !== false,
    questions: questions.map((q) => ({
      id: typeof q.id === 'string' ? q.id : newQuestionId(),
      prompt: typeof q.prompt === 'string' ? q.prompt : '',
      type: q.type === 'multi' ? 'multi' : q.type === 'text' ? 'text' : 'single',
      optionsText: Array.isArray(q.options) ? q.options.join('\n') : '',
    })),
  };
}

function formQuestionsToFirestore(formQuestions) {
  return formQuestions.map((q) => {
    const options =
      q.type === 'text'
        ? []
        : String(q.optionsText || '')
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
    return {
      id: q.id,
      prompt: String(q.prompt || '').trim(),
      type: q.type,
      options,
    };
  });
}

function formatAnswer(q, raw) {
  if (raw == null) return '—';
  if (q.type === 'multi' && Array.isArray(raw)) return raw.join(', ') || '—';
  if (typeof raw === 'string') return raw || '—';
  return String(raw);
}

function formatAnswerCsv(q, raw) {
  if (raw == null) return '';
  if (q.type === 'multi' && Array.isArray(raw)) return raw.join('; ');
  if (typeof raw === 'string') return raw;
  return String(raw);
}

function questionColumnHeader(q, index) {
  const p = String(q.prompt || '')
    .replace(/[\r\n]+/g, ' ')
    .trim();
  const short = p.length > 120 ? `${p.slice(0, 117)}…` : p;
  return `Q${index + 1}: ${short || q.id}`;
}

function csvFilenameBase(title) {
  const slug = String(title || 'survey')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `parent-survey-${slug || 'export'}-${stamp}`;
}

const SURVEY_OPTION_TOOLTIPS = {
  active:
    'When on, this survey appears in the parent portal under Team surveys. When off, families cannot see or open it.',
  liveResults:
    'When on, parents see live aggregate counts (charts). Tallies never list individual names on the family-facing page.',
  allowEdits:
    'When on, a family can change their answers after submitting. When off, each account may only submit once (no changes).',
  anonymous:
    'Parent-facing results never show who voted—only totals. Staff can still see which account submitted each row in Responses below.',
};

export default function ParentSurveysAdmin({ parents }) {
  const [surveys, setSurveys] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(() => ({
    title: '',
    description: '',
    active: false,
    showLiveResults: true,
    allowEdit: true,
    anonymousPublicResults: true,
    questions: [defaultQuestion()],
  }));
  const [saving, setSaving] = useState(false);
  const [responses, setResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [notice, setNotice] = useState(null);
  /** Which survey option help panel is open (click ? to toggle). */
  const [openSurveyHelp, setOpenSurveyHelp] = useState(null);

  useEffect(() => {
    if (openSurveyHelp == null) return undefined;
    const onDocMouseDown = (e) => {
      if (e.target.closest('.parent-surveys-admin__option-help-root')) return;
      setOpenSurveyHelp(null);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenSurveyHelp(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openSurveyHelp]);

  useEffect(() => {
    const qRef = query(collection(db, 'parentSurveys'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        setSurveys(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingList(false);
      },
      (err) => {
        console.error(err);
        setLoadingList(false);
        setNotice({ variant: 'error', message: err?.message || 'Failed to load surveys.' });
      }
    );
    return () => unsub();
  }, []);

  const selectedSurvey = useMemo(
    () => (selectedId && !isNew ? surveys.find((s) => s.id === selectedId) : null),
    [selectedId, isNew, surveys]
  );

  useEffect(() => {
    if (isNew) return;
    if (selectedSurvey) {
      setForm(surveyToForm(selectedSurvey));
    }
  }, [selectedSurvey, isNew]);

  const loadResponses = useCallback(async (surveyId) => {
    if (!surveyId || isNew) {
      setResponses([]);
      return;
    }
    setLoadingResponses(true);
    try {
      const snap = await getDocs(collection(db, 'parentSurveys', surveyId, 'responses'));
      const rows = snap.docs.map((d) => ({ parentUid: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = a.submittedAt?.toMillis?.() || 0;
        const tb = b.submittedAt?.toMillis?.() || 0;
        return tb - ta;
      });
      setResponses(rows);
    } catch (e) {
      console.error(e);
      setResponses([]);
    } finally {
      setLoadingResponses(false);
    }
  }, [isNew]);

  useEffect(() => {
    if (selectedId && !isNew) loadResponses(selectedId);
    else setResponses([]);
  }, [selectedId, isNew, loadResponses]);

  const firestoreQuestions = useMemo(() => formQuestionsToFirestore(form.questions), [form.questions]);

  const handleExportCsv = useCallback(() => {
    if (!selectedId || isNew || responses.length === 0) return;
    const qs = firestoreQuestions;
    const qHeaders = qs.map((q, i) => questionColumnHeader(q, i));
    const rows = responses.map((row) => {
      const { name, email } = parentRowFields(parents, row.parentUid);
      const out = {
        survey_id: selectedId,
        survey_title: form.title.trim(),
        parent_uid: row.parentUid,
        parent_name: name,
        parent_email: email,
        submitted_at:
          row.submittedAt && typeof row.submittedAt.toDate === 'function'
            ? row.submittedAt.toDate().toISOString()
            : '',
        updated_at:
          row.updatedAt && typeof row.updatedAt.toDate === 'function'
            ? row.updatedAt.toDate().toISOString()
            : '',
      };
      qs.forEach((q, i) => {
        out[qHeaders[i]] = formatAnswerCsv(q, row.answers && row.answers[q.id]);
      });
      return out;
    });
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${csvFilenameBase(form.title)}.csv`);
  }, [selectedId, isNew, responses, firestoreQuestions, form.title, parents]);

  const startNew = () => {
    setOpenSurveyHelp(null);
    setSelectedId(null);
    setIsNew(true);
    setForm({
      title: '',
      description: '',
      active: false,
      showLiveResults: true,
      allowEdit: true,
      anonymousPublicResults: true,
      questions: [defaultQuestion()],
    });
    setNotice(null);
  };

  const selectSurvey = (id) => {
    setOpenSurveyHelp(null);
    setIsNew(false);
    setSelectedId(id);
    setNotice(null);
  };

  const updateQuestion = (idx, patch) => {
    setForm((f) => {
      const questions = f.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
      return { ...f, questions };
    });
  };

  const addQuestion = () => {
    setForm((f) => ({ ...f, questions: [...f.questions, defaultQuestion()] }));
  };

  const removeQuestion = (idx) => {
    setForm((f) => ({
      ...f,
      questions: f.questions.length <= 1 ? f.questions : f.questions.filter((_, i) => i !== idx),
    }));
  };

  const moveQuestion = (idx, dir) => {
    setForm((f) => {
      const j = idx + dir;
      if (j < 0 || j >= f.questions.length) return f;
      const questions = f.questions.slice();
      const t = questions[idx];
      questions[idx] = questions[j];
      questions[j] = t;
      return { ...f, questions };
    });
  };

  const handleSave = async () => {
    const title = form.title.trim();
    if (!title) {
      setNotice({ variant: 'error', message: 'Title is required.' });
      return;
    }
    const questions = formQuestionsToFirestore(form.questions);
    if (questions.some((q) => !q.prompt)) {
      setNotice({ variant: 'error', message: 'Each question needs a prompt.' });
      return;
    }
    if (questions.some((q) => (q.type === 'single' || q.type === 'multi') && q.options.length === 0)) {
      setNotice({ variant: 'error', message: 'Choice questions need at least one option (one per line).' });
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const now = Timestamp.now();
      const payload = {
        title,
        description: form.description.trim(),
        active: form.active,
        showLiveResults: form.showLiveResults,
        allowEdit: form.allowEdit,
        anonymousPublicResults: form.anonymousPublicResults,
        questions,
        updatedAt: now,
      };
      if (isNew) {
        const ref = await addDoc(collection(db, 'parentSurveys'), {
          ...payload,
          createdAt: now,
        });
        setIsNew(false);
        setSelectedId(ref.id);
        setNotice({ variant: 'success', message: 'Survey created.' });
      } else if (selectedId) {
        await updateDoc(doc(db, 'parentSurveys', selectedId), payload);
        setNotice({ variant: 'success', message: 'Survey saved.' });
      }
    } catch (e) {
      console.error(e);
      setNotice({ variant: 'error', message: e?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAllowEdit = async () => {
    if (!selectedId || isNew || form.allowEdit) return;
    if (
      !window.confirm(
        'Turn on “Allow edits after submit” for this survey?\n\nFamilies who already submitted can change their answers in Team surveys (Update answers).'
      )
    ) {
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const now = Timestamp.now();
      await updateDoc(doc(db, 'parentSurveys', selectedId), {
        allowEdit: true,
        updatedAt: now,
      });
      setForm((f) => ({ ...f, allowEdit: true }));
      setNotice({
        variant: 'success',
        message: 'Families can now edit their submitted answers in the parent portal.',
      });
    } catch (e) {
      console.error(e);
      setNotice({ variant: 'error', message: e?.message || 'Could not enable edits.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSurvey = async () => {
    if (!selectedId || isNew) return;
    if (!window.confirm('Delete this survey and all responses? This cannot be undone.')) return;
    setOpenSurveyHelp(null);
    setSaving(true);
    setNotice(null);
    try {
      const respSnap = await getDocs(collection(db, 'parentSurveys', selectedId, 'responses'));
      let batch = writeBatch(db);
      let n = 0;
      const enqueueDelete = async (ref) => {
        if (n >= 400) {
          await batch.commit();
          batch = writeBatch(db);
          n = 0;
        }
        batch.delete(ref);
        n += 1;
      };
      for (const d of respSnap.docs) {
        await enqueueDelete(d.ref);
      }
      await enqueueDelete(doc(db, 'parentSurveys', selectedId, 'resultsTally', 'current'));
      await enqueueDelete(doc(db, 'parentSurveys', selectedId));
      await batch.commit();
      setSelectedId(null);
      setIsNew(false);
      setForm({
        title: '',
        description: '',
        active: false,
        showLiveResults: true,
        allowEdit: true,
        anonymousPublicResults: true,
        questions: [defaultQuestion()],
      });
      setNotice({ variant: 'success', message: 'Survey deleted.' });
    } catch (e) {
      console.error(e);
      setNotice({ variant: 'error', message: e?.message || 'Delete failed.' });
    } finally {
      setSaving(false);
    }
  };

  const canExport = !isNew && selectedId && responses.length > 0 && !loadingResponses;

  return (
    <div className="admin-section parent-surveys-admin">
      <h2 className="admin-main-section-title">Parent surveys</h2>
      <p className="parent-surveys-admin__intro">
        Build preference polls for families (tournament locations, schedule preferences, etc.). Parents submit answers in
        their portal under <strong style={{ color: '#ccc' }}>Team surveys</strong>. Live results show aggregate counts only
        — no names on the family-facing tallies. Staff can still see which account submitted each response below. When{' '}
        <strong style={{ color: '#ccc' }}>Allow edits after submit</strong> is on, families fix mistakes themselves with{' '}
        <strong style={{ color: '#ccc' }}>Update answers</strong> in their portal.
      </p>

      {notice && (
        <div
          className={`parent-surveys-admin__notice parent-surveys-admin__notice--${notice.variant === 'error' ? 'error' : 'success'}`}
          role={notice.variant === 'error' ? 'alert' : 'status'}
        >
          {notice.message}
        </div>
      )}

      <div className="parent-surveys-admin-grid">
        <aside className="parent-surveys-admin__sidebar" aria-label="Survey list">
          <p className="parent-surveys-admin__sidebar-title">All surveys</p>
          <button type="button" className="parent-surveys-admin__new-btn" onClick={startNew}>
            + New survey
          </button>
          {loadingList ? (
            <p style={{ color: '#999', fontSize: '0.9rem' }}>Loading…</p>
          ) : surveys.length === 0 ? (
            <p style={{ color: '#888', fontSize: '0.9rem' }}>No surveys yet.</p>
          ) : (
            <ul className="parent-surveys-admin__list">
              {surveys.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`parent-surveys-admin__list-item ${selectedId === s.id && !isNew ? 'is-active' : ''}`}
                    onClick={() => selectSurvey(s.id)}
                  >
                    <span className="parent-surveys-admin__list-item-title">{s.title || 'Untitled'}</span>
                    <span className="parent-surveys-admin__list-item-meta">
                      {s.active ? 'Active' : 'Inactive'}
                      {s.showLiveResults === false ? ' · results hidden' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <div className="parent-surveys-admin__editor">
          {(selectedId || isNew) && (
            <>
              <div className="parent-surveys-admin__toolbar">
                <button
                  type="button"
                  className="parent-surveys-admin__btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save survey'}
                </button>
                <button
                  type="button"
                  className="parent-surveys-admin__btn-secondary"
                  onClick={handleExportCsv}
                  disabled={saving || !canExport}
                  title={!canExport ? 'Save the survey and wait for responses to export' : 'Download responses as CSV'}
                >
                  Export CSV
                </button>
                <span className="parent-surveys-admin__toolbar-spacer" aria-hidden />
                {!isNew && selectedId && (
                  <button
                    type="button"
                    className="parent-surveys-admin__btn-danger"
                    onClick={handleDeleteSurvey}
                    disabled={saving}
                  >
                    Delete survey
                  </button>
                )}
              </div>

              <label className="parent-surveys-admin__field">
                <span className="parent-surveys-admin__field-label">Title</span>
                <input
                  type="text"
                  className="admin-input"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </label>
              <label className="parent-surveys-admin__field">
                <span className="parent-surveys-admin__field-label">Description (optional)</span>
                <textarea
                  className="admin-input"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                />
              </label>

              <div className="parent-surveys-admin__options-grid" role="group" aria-label="Survey visibility and results">
                <div className="parent-surveys-admin__option-help-root">
                  <label className="parent-surveys-admin__option-label">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    />
                    <span className="parent-surveys-admin__option-phrase">Active</span>
                  </label>
                  <div className="parent-surveys-admin__option-help-wrap">
                    <button
                      type="button"
                      className={`parent-surveys-admin__option-tip${openSurveyHelp === 'active' ? ' is-open' : ''}`}
                      aria-label="Explain Active"
                      aria-expanded={openSurveyHelp === 'active'}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenSurveyHelp((k) => (k === 'active' ? null : 'active'));
                      }}
                    >
                      ?
                    </button>
                    {openSurveyHelp === 'active' && (
                      <div className="parent-surveys-admin__option-tip-panel" role="tooltip">
                        {SURVEY_OPTION_TOOLTIPS.active}
                      </div>
                    )}
                  </div>
                </div>
                <div className="parent-surveys-admin__option-help-root">
                  <label className="parent-surveys-admin__option-label">
                    <input
                      type="checkbox"
                      checked={form.showLiveResults}
                      onChange={(e) => setForm((f) => ({ ...f, showLiveResults: e.target.checked }))}
                    />
                    <span className="parent-surveys-admin__option-phrase">Live results</span>
                  </label>
                  <div className="parent-surveys-admin__option-help-wrap">
                    <button
                      type="button"
                      className={`parent-surveys-admin__option-tip${openSurveyHelp === 'liveResults' ? ' is-open' : ''}`}
                      aria-label="Explain Live results"
                      aria-expanded={openSurveyHelp === 'liveResults'}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenSurveyHelp((k) => (k === 'liveResults' ? null : 'liveResults'));
                      }}
                    >
                      ?
                    </button>
                    {openSurveyHelp === 'liveResults' && (
                      <div className="parent-surveys-admin__option-tip-panel" role="tooltip">
                        {SURVEY_OPTION_TOOLTIPS.liveResults}
                      </div>
                    )}
                  </div>
                </div>
                <div className="parent-surveys-admin__option-help-root">
                  <label className="parent-surveys-admin__option-label">
                    <input
                      type="checkbox"
                      checked={form.allowEdit}
                      onChange={(e) => setForm((f) => ({ ...f, allowEdit: e.target.checked }))}
                    />
                    <span className="parent-surveys-admin__option-phrase">Allow edits after submit</span>
                  </label>
                  <div className="parent-surveys-admin__option-help-wrap">
                    <button
                      type="button"
                      className={`parent-surveys-admin__option-tip${openSurveyHelp === 'allowEdits' ? ' is-open' : ''}`}
                      aria-label="Explain Allow edits after submit"
                      aria-expanded={openSurveyHelp === 'allowEdits'}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenSurveyHelp((k) => (k === 'allowEdits' ? null : 'allowEdits'));
                      }}
                    >
                      ?
                    </button>
                    {openSurveyHelp === 'allowEdits' && (
                      <div className="parent-surveys-admin__option-tip-panel" role="tooltip">
                        {SURVEY_OPTION_TOOLTIPS.allowEdits}
                      </div>
                    )}
                  </div>
                </div>
                <div className="parent-surveys-admin__option-help-root">
                  <label className="parent-surveys-admin__option-label">
                    <input
                      type="checkbox"
                      checked={form.anonymousPublicResults}
                      onChange={(e) => setForm((f) => ({ ...f, anonymousPublicResults: e.target.checked }))}
                    />
                    <span className="parent-surveys-admin__option-phrase">Anonymous</span>
                  </label>
                  <div className="parent-surveys-admin__option-help-wrap">
                    <button
                      type="button"
                      className={`parent-surveys-admin__option-tip${openSurveyHelp === 'anonymous' ? ' is-open' : ''}`}
                      aria-label="Explain Anonymous"
                      aria-expanded={openSurveyHelp === 'anonymous'}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenSurveyHelp((k) => (k === 'anonymous' ? null : 'anonymous'));
                      }}
                    >
                      ?
                    </button>
                    {openSurveyHelp === 'anonymous' && (
                      <div className="parent-surveys-admin__option-tip-panel" role="tooltip">
                        {SURVEY_OPTION_TOOLTIPS.anonymous}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <h3 className="parent-surveys-admin__section-title">Questions</h3>
              {form.questions.map((q, idx) => (
                <div key={q.id} className="parent-surveys-admin__question-card">
                  <div className="parent-surveys-admin__question-head">
                    <span className="parent-surveys-admin__question-index">Question {idx + 1}</span>
                    <div className="parent-surveys-admin__question-actions">
                      <button
                        type="button"
                        className="parent-surveys-admin__icon-btn"
                        onClick={() => moveQuestion(idx, -1)}
                        disabled={idx === 0}
                        title="Move up"
                        aria-label="Move question up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="parent-surveys-admin__icon-btn"
                        onClick={() => moveQuestion(idx, 1)}
                        disabled={idx === form.questions.length - 1}
                        title="Move down"
                        aria-label="Move question down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="parent-surveys-admin__icon-btn parent-surveys-admin__icon-btn--danger"
                        onClick={() => removeQuestion(idx)}
                        title="Remove question"
                        aria-label="Remove question"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <label className="parent-surveys-admin__field" style={{ marginBottom: 10 }}>
                    <span className="parent-surveys-admin__field-label">Prompt</span>
                    <input
                      type="text"
                      className="admin-input"
                      value={q.prompt}
                      onChange={(e) => updateQuestion(idx, { prompt: e.target.value })}
                    />
                  </label>
                  <label className="parent-surveys-admin__field" style={{ marginBottom: 10 }}>
                    <span className="parent-surveys-admin__field-label">Type</span>
                    <select
                      className="admin-input"
                      value={q.type}
                      onChange={(e) => updateQuestion(idx, { type: e.target.value })}
                      style={{ maxWidth: 220 }}
                    >
                      <option value="single">Single choice</option>
                      <option value="multi">Multiple choice</option>
                      <option value="text">Short text</option>
                    </select>
                  </label>
                  {(q.type === 'single' || q.type === 'multi') && (
                    <label className="parent-surveys-admin__field" style={{ marginBottom: 0 }}>
                      <span className="parent-surveys-admin__field-label">Options (one per line)</span>
                      <textarea
                        className="admin-input"
                        value={q.optionsText}
                        onChange={(e) => updateQuestion(idx, { optionsText: e.target.value })}
                        rows={4}
                      />
                    </label>
                  )}
                </div>
              ))}
              <button type="button" className="parent-surveys-admin__btn-secondary parent-surveys-admin__add-q" onClick={addQuestion}>
                + Add question
              </button>

              {!isNew && selectedId && (
                <>
                  <h3 className="parent-surveys-admin__section-title">Responses (staff only)</h3>
                  {!form.allowEdit && responses.length > 0 ? (
                    <div className="parent-surveys-admin__responses-hint">
                      <p>
                        Edits are off — families cannot change answers after they submit. Turn on edits so they can fix
                        mistakes themselves.
                      </p>
                      <button
                        type="button"
                        className="parent-surveys-admin__btn-secondary"
                        onClick={handleEnableAllowEdit}
                        disabled={saving}
                      >
                        Enable edits for families
                      </button>
                    </div>
                  ) : null}
                  {form.allowEdit && responses.length > 0 ? (
                    <p className="parent-surveys-admin__responses-edits-on" role="status">
                      Allow edits after submit is on — families can use <strong>Update answers</strong> in Team surveys.
                    </p>
                  ) : null}
                  {loadingResponses ? (
                    <p style={{ color: '#999' }}>Loading responses…</p>
                  ) : responses.length === 0 ? (
                    <p className="parent-surveys-admin__empty-state">No responses yet. Export CSV will be available once families submit.</p>
                  ) : (
                    <div className="parent-surveys-admin__table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Parent</th>
                            {form.questions.map((qu) => (
                              <th key={qu.id}>{qu.prompt || qu.id}</th>
                            ))}
                            <th>Submitted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {responses.map((row) => (
                            <tr key={row.parentUid}>
                              <td style={{ whiteSpace: 'nowrap' }}>{parentLabel(parents, row.parentUid)}</td>
                              {form.questions.map((qu) => (
                                <td key={qu.id}>{formatAnswer(qu, row.answers && row.answers[qu.id])}</td>
                              ))}
                              <td style={{ fontSize: '0.85rem', color: '#999' }}>
                                {row.submittedAt?.toDate ? row.submittedAt.toDate().toLocaleString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {!selectedId && !isNew && (
            <p className="parent-surveys-admin__empty-state">Select a survey from the list or create a new one.</p>
          )}
        </div>
      </div>
    </div>
  );
}
